import React, { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Users2, Award, DollarSign, X, AlertTriangle } from "lucide-react";
import styles from "../hrms.module.scss";
import Popup from "components/minor/popup/popup";
import { apiBaseUrl } from '../../../../../../../services/config';
import { useTheme } from "context/ThemeContext";
const API = apiBaseUrl;
const ax  = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` } });
const fmt = (n) => `₹${Math.round(n||0).toLocaleString("en-IN")}`;

const RATINGS = ["Exceeds Expectations","Meets Expectations","Needs Improvement","Unacceptable"];

export default function PerformanceAppraisals() {
  const { T } = useTheme();
  const RATING_COLOR = {
    "Exceeds Expectations":T.green,"Meets Expectations":T.cyan,
    "Needs Improvement":T.amber,"Unacceptable":T.red,
  };
  const STATUS_COLOR = {
    "Pending":T.muted,"Self-Submitted":T.amber,
    "Manager-Reviewed":T.cyan,"HR-Finalized":T.green,
  };
  const inp = (err=false) => ({
    width:"100%", background:T.inp_bg,
    border:`1px solid ${err?T.red:T.border}`,
    borderRadius:8, color:T.text, fontSize:13, padding:"9px 12px",
    outline:"none", fontFamily:"Outfit,sans-serif", boxSizing:"border-box",
  });
  const lbl = { fontSize:11, color:T.muted, display:"block", marginBottom:4, textTransform:"uppercase", letterSpacing:"0.07em" };

  const [tab,        setTab]        = useState("cycles");
  const [cycles,     setCycles]     = useState([]);
  const [appraisals, setAppraisals] = useState([]);
  const [employees,  setEmployees]  = useState([]);
  const [categories, setCategories] = useState([]);
  const [selCycle,   setSelCycle]   = useState(null);
  const [selAppraisal,setSelAppraisal]=useState(null);
  const [loading,    setLoading]    = useState(true);
  const [toast,      setToast]      = useState("");

  const [cOpen, setCOpen] = useState(false);
  const [cData, setCData] = useState({ name:"", period_type:"Annual", review_period:"", start_date:"", end_date:"" });
  const [aOpen, setAOpen] = useState(false);
  const [aCycleId, setACycleId] = useState(null);
  const [aSelEmps, setASelEmps] = useState([]);
  const [fOpen, setFOpen] = useState(false);
  const [fAppId,setFAppId]= useState(null);
  const [fData, setFData] = useState({ overall_rating:"Meets Expectations", increment_percent:0, increment_effective:"", hr_notes:"" });

  const flash = (msg) => { setToast(msg); setTimeout(()=>setToast(""),3500); };

  const load = async () => {
    setLoading(true);
    try {
      const [cRes, eRes, catRes] = await Promise.all([
        axios.get(`${API}/performance/cycles`,     ax()),
        axios.get(`${API}/employees/`,             ax()),
        axios.get(`${API}/performance/categories`, ax()),
      ]);
      setCycles(cRes.data   || []);
      setEmployees(eRes.data|| []);
      setCategories(catRes.data||[]);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadAppraisals = async (cycleId=null) => {
    try {
      const url = `${API}/performance/appraisals${cycleId?`?cycle_id=${cycleId}`:""}`;
      const r = await axios.get(url, ax());
      setAppraisals(r.data||[]);
    } catch(e) { console.error(e); }
  };

  useEffect(() => { load(); }, []);

  const saveCycle = async () => {
    try {
      await axios.post(`${API}/performance/cycles`, cData, ax());
      setCOpen(false); load(); flash("✅ Cycle created");
    } catch(e) { flash("❌ Error"); }
  };

  const setCycleStatus = async (id, status) => {
    try {
      await axios.put(`${API}/performance/cycles/${id}/status?status=${status}`, {}, ax());
      load(); flash(`✅ Cycle ${status}`);
    } catch(e) { flash("❌ Error"); }
  };

  const assignEmployees = async () => {
    try {
      await axios.post(`${API}/performance/cycles/${aCycleId}/assign`, { employee_ids:aSelEmps }, ax());
      setAOpen(false); loadAppraisals(aCycleId); flash("✅ Employees assigned");
    } catch(e) { flash("❌ Error"); }
  };

  const seedDefaults = async () => {
    try {
      await axios.post(`${API}/performance/categories/seed-defaults`, {}, ax());
      load(); flash("✅ Default categories seeded");
    } catch(e) { flash("ℹ️ " + (e.response?.data?.detail||"Already seeded")); }
  };

  const finalizeAppraisal = async () => {
    try {
      await axios.put(`${API}/performance/appraisals/${fAppId}/finalize`, fData, ax());
      setFOpen(false); flash("✅ Appraisal finalized"); loadAppraisals(selCycle);
    } catch(e) { flash("❌ " + (e.response?.data?.detail||"Error")); }
  };

  const applyIncrement = async (aid) => {
    try {
      const r = await axios.post(`${API}/performance/appraisals/${aid}/apply-increment`, {}, ax());
      flash(`✅ Increment applied — New CTC: ${fmt(r.data.new_ctc)}`);
      loadAppraisals(selCycle);
    } catch(e) { flash("❌ " + (e.response?.data?.detail||"Error")); }
  };

  const TabBtn = ({ id, label }) => (
    <button onClick={() => { setTab(id); if(id==="appraisals") loadAppraisals(selCycle); }}
      style={{ padding:"10px 18px", fontSize:13, fontWeight:500, fontFamily:"Outfit,sans-serif",
        color:tab===id?T.cyan:T.muted, background:"none", border:"none",
        borderBottom:tab===id?`2px solid ${T.cyan}`:"2px solid transparent", cursor:"pointer" }}>
      {label}
    </button>
  );

  return (
    <div className={styles.pageRoot}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.badge}>PERFORMANCE</div>
          <h2>Appraisals</h2>
          <p>Manage appraisal cycles and employee reviews</p>
        </div>
        <div className={styles.headerRight}>
          {categories.length === 0 && (
            <button className={styles.btnSecondary} onClick={seedDefaults}>Seed Categories</button>
          )}
          <button className={styles.btnPrimary}
            onClick={() => { setCData({ name:"", period_type:"Annual", review_period:"", start_date:"", end_date:"" }); setCOpen(true); }}>
            <Plus size={14}/> New Cycle
          </button>
        </div>
      </div>

      <div className={styles.card}>
        <div style={{ display:"flex", borderBottom:`1px solid ${T.border}` }}>
          <TabBtn id="cycles"     label="Cycles"/>
          <TabBtn id="appraisals" label="Appraisals"/>
          <TabBtn id="setup"      label="Categories"/>
        </div>

        {loading ? <div className={styles.loader}><div className={styles.spinner}/>Loading…</div> : (
          <>
          {/* ── CYCLES ── */}
          {tab === "cycles" && (
            <div style={{ padding:20 }}>
              {cycles.length === 0 ? (
                <div style={{ textAlign:"center", padding:40, color:T.muted }}>No cycles yet — create one above</div>
              ) : cycles.map(c => (
                <div key={c.id} style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:10, padding:16, marginBottom:12 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:15, fontWeight:700, color:T.text }}>{c.name}</div>
                      <div style={{ fontSize:12, color:T.muted, marginTop:3 }}>{c.period_type} · {c.review_period||"—"} · {c.start_date||"?"} → {c.end_date||"?"}</div>
                      <div style={{ display:"flex", gap:12, marginTop:8, fontSize:12, flexWrap:"wrap" }}>
                        {[["Total",c.stats?.total,T.muted],["Pending",c.stats?.pending,T.muted],
                          ["Submitted",c.stats?.submitted,T.amber],["Reviewed",c.stats?.manager_reviewed,T.cyan],
                          ["Finalized",c.stats?.finalized,T.green]].map(([l,v,col]) => (
                          <span key={l} style={{ color:col }}>{l}: <strong style={{ fontFamily:"JetBrains Mono,monospace" }}>{v||0}</strong></span>
                        ))}
                      </div>
                    </div>
                    <span style={{ fontSize:11.5, padding:"3px 10px", borderRadius:20, fontWeight:600,
                      background:c.status==="Active"?`${T.green}1A`:c.status==="Closed"?`${T.red}1A`:`${T.amber}1A`,
                      color:c.status==="Active"?T.green:c.status==="Closed"?T.red:T.amber }}>
                      {c.status}
                    </span>
                    <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                      {c.status==="Draft"  && <button className={styles.btnSm} style={{ color:T.green, borderColor:`${T.green}40` }} onClick={()=>setCycleStatus(c.id,"Active")}>Activate</button>}
                      {c.status==="Active" && <button className={styles.btnSm} style={{ color:T.red, borderColor:`${T.red}40` }} onClick={()=>setCycleStatus(c.id,"Closed")}>Close</button>}
                      <button className={styles.btnSm} onClick={()=>{ setACycleId(c.id); setASelEmps([]); setAOpen(true); }}>
                        <Users2 size={12}/> Assign
                      </button>
                      <button className={styles.btnSm} style={{ color:T.cyan }}
                        onClick={()=>{ setSelCycle(c.id); setTab("appraisals"); loadAppraisals(c.id); }}>
                        View →
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── APPRAISALS ── */}
          {tab === "appraisals" && (
            <div style={{ padding:20 }}>
              <div style={{ display:"flex", gap:10, marginBottom:16 }}>
                <select value={selCycle||""} onChange={e=>{ setSelCycle(e.target.value||null); loadAppraisals(e.target.value||null); }}
                  style={{ ...inp(), width:"auto", minWidth:200 }}>
                  <option value="">All Cycles</option>
                  {cycles.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              {appraisals.length === 0 ? (
                <div style={{ textAlign:"center", padding:40, color:T.muted }}>No appraisals — assign employees to a cycle first</div>
              ) : (
                <div className={styles.tableWrap}>
                  <table className={styles.darkTable}>
                    <thead><tr><th>Employee</th><th>Cycle</th><th>Status</th><th>Rating</th><th>Increment</th><th>New CTC</th><th>Actions</th></tr></thead>
                    <tbody>
                      {appraisals.map(a => (
                        <tr key={a.id}>
                          <td><div style={{ fontWeight:500 }}>{a.employee_name}</div><div style={{ fontSize:11, color:T.muted }}>{a.employee_code}</div></td>
                          <td className={styles.dim}>{a.cycle_name}</td>
                          <td>
                            <span style={{ fontSize:11.5, padding:"2px 9px", borderRadius:20, fontWeight:600,
                              background:`${STATUS_COLOR[a.status]||T.muted}20`,
                              color:STATUS_COLOR[a.status]||T.muted }}>
                              {a.status.replace(/-/g," ")}
                            </span>
                          </td>
                          <td>
                            {a.overall_rating
                              ? <span style={{ fontSize:12, padding:"2px 8px", borderRadius:8, background:`${RATING_COLOR[a.overall_rating]}15`, color:RATING_COLOR[a.overall_rating] }}>{a.overall_rating}</span>
                              : <span className={styles.dim}>—</span>}
                          </td>
                          <td>{a.increment_percent>0?<span style={{ color:T.green, fontFamily:"JetBrains Mono,monospace", fontWeight:700 }}>{a.increment_percent}%</span>:<span className={styles.dim}>—</span>}</td>
                          <td>{a.new_ctc>0?<span style={{ fontFamily:"JetBrains Mono,monospace", color:T.amber }}>{fmt(a.new_ctc/12)}/mo</span>:<span className={styles.dim}>—</span>}</td>
                          <td>
                            <div style={{ display:"flex", gap:5 }}>
                              <button className={styles.btnSm} onClick={()=>setSelAppraisal(a)}>View</button>
                              {a.status==="Manager-Reviewed" && !a.overall_rating && (
                                <button className={styles.btnSm} style={{ color:T.green, borderColor:`${T.green}40` }}
                                  onClick={()=>{ setFAppId(a.id); setFData({ overall_rating:"Meets Expectations", increment_percent:0, increment_effective:"", hr_notes:"" }); setFOpen(true); }}>
                                  Finalize
                                </button>
                              )}
                              {a.status==="HR-Finalized" && !a.salary_updated && (
                                <button className={styles.btnSm} style={{ color:T.amber, borderColor:`${T.amber}40` }}
                                  onClick={()=>applyIncrement(a.id)}>
                                  <DollarSign size={11}/> Apply
                                </button>
                              )}
                              {a.salary_updated && <span style={{ fontSize:11, color:T.green }}>✅ Applied</span>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── CATEGORIES SETUP ── */}
          {tab === "setup" && (
            <div style={{ padding:20 }}>
              {categories.length === 0 ? (
                <div style={{ textAlign:"center", padding:40 }}>
                  <button onClick={seedDefaults} className={styles.btnPrimary}>Seed Default Categories</button>
                </div>
              ) : categories.map(cat => (
                <div key={cat.id} style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:10, padding:16, marginBottom:10 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:T.text, marginBottom:8 }}>
                    {cat.name}
                    <span style={{ fontSize:11, marginLeft:8, padding:"2px 8px", borderRadius:8, background:`${T.purple}1A`, color:T.purple }}>{cat.type}</span>
                  </div>
                  {cat.questions.map(q => (
                    <div key={q.id} style={{ padding:"7px 10px", background:T.surface_faint, borderRadius:7, marginBottom:5 }}>
                      <div style={{ fontSize:13, color:T.text }}>{q.title}</div>
                      {q.description && <div style={{ fontSize:11.5, color:T.muted, marginTop:2 }}>{q.description}</div>}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
          </>
        )}
      </div>

      {/* View appraisal detail modal */}
      {selAppraisal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:9998, overflowY:"auto", fontFamily:"Outfit,sans-serif" }}
          onClick={()=>setSelAppraisal(null)}>
          <div style={{ background:T.card, border:`1px solid ${T.border_strong}`, borderRadius:14, width:"min(780px,95vw)", margin:"40px auto", overflow:"hidden" }}
            onClick={e=>e.stopPropagation()}>
            <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between" }}>
              <div>
                <h3 style={{ margin:0, fontSize:16, fontWeight:600, color:T.text }}>{selAppraisal.employee_name}</h3>
                <div style={{ fontSize:12, color:T.muted, marginTop:2 }}>{selAppraisal.designation} · {selAppraisal.cycle_name}</div>
              </div>
              <button onClick={()=>setSelAppraisal(null)} style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }}><X size={16}/></button>
            </div>
            <div style={{ padding:24 }}>
              {Object.entries((() => {
                const cats = {};
                (selAppraisal.responses||[]).forEach(r => { const c=r.category||"General"; if(!cats[c]) cats[c]=[]; cats[c].push(r); });
                return cats;
              })()).map(([catName, responses]) => (
                <div key={catName} style={{ marginBottom:20 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:T.cyan, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10, paddingBottom:6, borderBottom:`1px solid ${T.border}` }}>{catName}</div>
                  {responses.map(r => (
                    <div key={r.id} style={{ background:T.surface_faint, borderRadius:9, padding:14, marginBottom:8 }}>
                      <div style={{ fontSize:13, fontWeight:600, color:T.text, marginBottom:10 }}>{r.question_title}</div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                        <div style={{ background:`${T.cyan}0A`, borderRadius:7, padding:10, border:`1px solid ${T.border}` }}>
                          <div style={{ fontSize:10.5, color:T.cyan, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:5 }}>Self Evaluation</div>
                          <div style={{ fontSize:12.5, color:T.text, lineHeight:1.6 }}>{r.self_evaluation||<em style={{color:T.dim}}>Not filled</em>}</div>
                          {r.self_rating && <div style={{ marginTop:6 }}><span style={{ fontSize:11, padding:"2px 8px", borderRadius:8, background:`${RATING_COLOR[r.self_rating]||T.muted}15`, color:RATING_COLOR[r.self_rating]||T.muted }}>{r.self_rating}</span></div>}
                        </div>
                        <div style={{ background:`${T.purple}0A`, borderRadius:7, padding:10, border:`1px solid ${T.purple}14` }}>
                          <div style={{ fontSize:10.5, color:T.purple, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:5 }}>Manager Evaluation</div>
                          <div style={{ fontSize:12.5, color:T.text, lineHeight:1.6 }}>{r.manager_evaluation||<em style={{color:T.dim}}>Pending</em>}</div>
                          {r.manager_rating && <div style={{ marginTop:6 }}><span style={{ fontSize:11, padding:"2px 8px", borderRadius:8, background:`${RATING_COLOR[r.manager_rating]||T.muted}15`, color:RATING_COLOR[r.manager_rating]||T.muted }}>{r.manager_rating}</span></div>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
              {selAppraisal.overall_rating && (
                <div style={{ background:`${T.green}0F`, border:`1px solid ${T.green}1F`, borderRadius:9, padding:14, display:"flex", gap:20, flexWrap:"wrap", marginTop:8 }}>
                  {[["Overall Rating",selAppraisal.overall_rating,RATING_COLOR[selAppraisal.overall_rating]||T.green],
                    ["Increment",`${selAppraisal.increment_percent}%`,T.amber],
                    ["New CTC",fmt(selAppraisal.new_ctc),T.green],
                    ["Effective",selAppraisal.increment_effective||"—",T.muted]].map(([l,v,c])=>(
                    <div key={l}><div style={{ fontSize:10.5, color:T.dim, textTransform:"uppercase", letterSpacing:"0.08em" }}>{l}</div>
                      <div style={{ fontSize:14, fontWeight:700, fontFamily:"JetBrains Mono,monospace", color:c, marginTop:3 }}>{v}</div></div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* New Cycle modal */}
      {cOpen && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9998, display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={()=>setCOpen(false)}>
          <div style={{ background:T.card, border:`1px solid ${T.border_strong}`, borderRadius:14, width:480, fontFamily:"Outfit,sans-serif", overflow:"hidden" }}
            onClick={e=>e.stopPropagation()}>
            <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between" }}>
              <h3 style={{ margin:0, fontSize:15, fontWeight:600, color:T.text }}>New Appraisal Cycle</h3>
              <button onClick={()=>setCOpen(false)} style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }}><X size={15}/></button>
            </div>
            <div style={{ padding:22, display:"flex", flexDirection:"column", gap:13 }}>
              <div><label style={lbl}>Name *</label><input value={cData.name} onChange={e=>setCData({...cData,name:e.target.value})} placeholder="e.g. Annual Review 2025-26" style={inp(!cData.name)}/></div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div><label style={lbl}>Type</label>
                  <select value={cData.period_type} onChange={e=>setCData({...cData,period_type:e.target.value})} style={inp()}>
                    {["Annual","H1","H2","Q1","Q2","Q3","Q4"].map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
                <div><label style={lbl}>Review Period</label><input value={cData.review_period} onChange={e=>setCData({...cData,review_period:e.target.value})} placeholder="Apr 2025 - Mar 2026" style={inp()}/></div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div><label style={lbl}>Start Date</label><input type="date" value={cData.start_date} onChange={e=>setCData({...cData,start_date:e.target.value})} style={inp()}/></div>
                <div><label style={lbl}>End Date</label><input type="date" value={cData.end_date} onChange={e=>setCData({...cData,end_date:e.target.value})} style={inp()}/></div>
              </div>
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end", paddingTop:8, borderTop:`1px solid ${T.border}` }}>
                <button onClick={()=>setCOpen(false)} className={styles.btnSecondary}>Cancel</button>
                <button onClick={saveCycle} className={styles.btnPrimary}>Create</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign employees modal */}
      {aOpen && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9998, display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={()=>setAOpen(false)}>
          <div style={{ background:T.card, border:`1px solid ${T.border_strong}`, borderRadius:14, width:440, fontFamily:"Outfit,sans-serif", overflow:"hidden" }}
            onClick={e=>e.stopPropagation()}>
            <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between" }}>
              <h3 style={{ margin:0, fontSize:15, fontWeight:600, color:T.text }}>Assign Employees</h3>
              <button onClick={()=>setAOpen(false)} style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }}><X size={15}/></button>
            </div>
            <div style={{ padding:20 }}>
              <div style={{ display:"flex", gap:8, marginBottom:10 }}>
                <button onClick={()=>setASelEmps(employees.map(e=>e.id))} style={{ fontSize:12, padding:"4px 10px", borderRadius:7, border:`1px solid ${T.cyan}26`, background:"none", color:T.cyan, cursor:"pointer" }}>Select All</button>
                <button onClick={()=>setASelEmps([])} style={{ fontSize:12, padding:"4px 10px", borderRadius:7, border:`1px solid ${T.border}`, background:"none", color:T.muted, cursor:"pointer" }}>Clear</button>
              </div>
              <div style={{ maxHeight:280, overflowY:"auto" }}>
                {employees.map(e => (
                  <div key={e.id} onClick={()=>setASelEmps(p=>p.includes(e.id)?p.filter(x=>x!==e.id):[...p,e.id])}
                    style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", borderRadius:7, marginBottom:3,
                      background:aSelEmps.includes(e.id)?`${T.cyan}12`:T.surface_faint,
                      border:`1px solid ${aSelEmps.includes(e.id)?`${T.cyan}33`:"transparent"}`, cursor:"pointer" }}>
                    <div style={{ width:18, height:18, borderRadius:4, border:`2px solid ${aSelEmps.includes(e.id)?T.cyan:T.dim}`,
                      background:aSelEmps.includes(e.id)?T.cyan:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:11, fontWeight:900, color:"#fff" }}>
                      {aSelEmps.includes(e.id)&&"✓"}
                    </div>
                    <div>
                      <div style={{ fontSize:13, fontWeight:500, color:T.text }}>{e.first_name} {e.last_name}</div>
                      <div style={{ fontSize:11, color:T.muted }}>{e.employee_code}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end", paddingTop:12, borderTop:`1px solid ${T.border}`, marginTop:12 }}>
                <button onClick={()=>setAOpen(false)} className={styles.btnSecondary}>Cancel</button>
                <button onClick={assignEmployees} className={styles.btnPrimary}><Users2 size={13}/> Assign {aSelEmps.length}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Finalize modal */}
      {fOpen && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9998, display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={()=>setFOpen(false)}>
          <div style={{ background:T.card, border:`1px solid ${T.green}33`, borderRadius:14, width:460, fontFamily:"Outfit,sans-serif", overflow:"hidden" }}
            onClick={e=>e.stopPropagation()}>
            <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between" }}>
              <h3 style={{ margin:0, fontSize:15, fontWeight:600, color:T.text }}>Finalize Appraisal</h3>
              <button onClick={()=>setFOpen(false)} style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }}><X size={15}/></button>
            </div>
            <div style={{ padding:22, display:"flex", flexDirection:"column", gap:13 }}>
              <div><label style={lbl}>Overall Rating *</label>
                <select value={fData.overall_rating} onChange={e=>setFData({...fData,overall_rating:e.target.value})} style={inp()}>
                  {RATINGS.map(r=><option key={r}>{r}</option>)}
                </select>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div><label style={lbl}>Increment %</label><input type="number" min={0} max={100} step={0.5} value={fData.increment_percent} onChange={e=>setFData({...fData,increment_percent:parseFloat(e.target.value)||0})} style={inp()}/></div>
                <div><label style={lbl}>Effective From</label><input type="date" value={fData.increment_effective} onChange={e=>setFData({...fData,increment_effective:e.target.value})} style={inp()}/></div>
              </div>
              <div><label style={lbl}>HR Notes</label><textarea value={fData.hr_notes} onChange={e=>setFData({...fData,hr_notes:e.target.value})} rows={2} style={{...inp(),resize:"vertical"}}/></div>
              <div style={{ background:`${T.amber}0F`, border:`1px solid ${T.amber}1F`, borderRadius:8, padding:"9px 14px", fontSize:12, color:T.amber, display:"flex", gap:7 }}>
                <AlertTriangle size={13} style={{ flexShrink:0, marginTop:1 }}/>
                After finalizing, click "Apply" in the table to update salary and recompute TDS.
              </div>
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end", paddingTop:8, borderTop:`1px solid ${T.border}` }}>
                <button onClick={()=>setFOpen(false)} className={styles.btnSecondary}>Cancel</button>
                <button onClick={finalizeAppraisal} className={styles.btnPrimary}><Award size={13}/> Finalize</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{ position:"fixed", bottom:28, left:"50%", transform:"translateX(-50%)", padding:"10px 22px", borderRadius:10, fontSize:13, fontWeight:600, zIndex:9999,
          background:T.card, border:`1px solid ${toast.startsWith("✅")?`${T.green}4D`:`${T.red}4D`}`,
          color:toast.startsWith("✅")?T.green:T.red, boxShadow:T.shadow, fontFamily:"Outfit,sans-serif" }}>
          {toast}
        </div>
      )}
    </div>
  );
}
